import React, { useEffect } from 'react'

import {
  Breadcrumbs,
  LayoutContainer as Container,
  HeadingXLarge,
  HeadingLarge,
  HeadingMedium,
  HeadingSmall,
  HeadingXSmall,
} from '@pb/components'
import {
  HeadingLarge as HeadingLargeNS,
  HeadingXLarge as HeadingXLargeNS,
  Container as ContainerNS,
} from '@hm-group/fabric-components'
import {
  getLabelFromStorage,
  getLocale,
  Breakpoints,
  useMediaQuery,
  currentBrand,
  getFixedHeaderHeight,
  isNorthstarBrand,
} from '@pb/utils'
import { useFeature } from '@optimizely/react-sdk'
import cn from 'classnames'
import { useSelector } from 'react-redux'

import appSelectors from 'App/duck/selectors'
import optimizelyConstants from 'constants/optimizely'

import { BRAND_OTHER_STORIES } from '../../constants'
import FavouritesError from '../FavouritesError'
import FavouritesItemsList from '../FavouritesItemList'
import Stylehub from '../Stylehub'
import styles from './Overview.scss'

const Overview: React.FC = () => {
  const shouldEnabledNS = isNorthstarBrand && process.env.NODE_ENVIRONMENT === 'development'
  // Styleboards
  const isStyleboardHybrisEnabled = useSelector(appSelectors.getIsStyleboardEnabled)
  const isStyleboardsEnabled =
    process.env.IS_STYLEBOARD_ENABLED === 'enabled' && isStyleboardHybrisEnabled

  // MyCloset
  const [isMyCloset, { myclosetflag }] = useFeature(optimizelyConstants.MY_CLOSET_FLAG)
  const showMycloset =
    (isMyCloset && myclosetflag) || myclosetflag === localStorage.getItem('myclosetflag')
  const isMyClosetEnabled = process.env.IS_MY_CLOSET_ENABLED === 'enabled' && showMycloset

  const breadcrumbsLinks = [
    { href: `/${getLocale()}`, label: getLabelFromStorage('breadcrumb.home') },
    {
      label: getLabelFromStorage('favorites.page.wishlist'),
    },
  ]
  const MemoizedBreadcrumbs: React.FC<any> = React.memo((props) => <Breadcrumbs {...props} />)

  const HeadingTextSize = process.env.FAVOURITES_OVERVIEW_HEADING_SIZE || 'large'

  const isBreadCrumbFullWidth = process.env.IS_BREADCRUMB_FULL_WIDTH === 'enabled'
  const isBreadCrumbVisibleSmallScreen =
    process.env.IS_BREADCRUMB_VISIBLE_ON_SMALL_SCREEN === 'enabled'

  const isLargeBreakpoint = useMediaQuery(Breakpoints.LARGE)
  const shouldHideBreadcrumbs = isBreadCrumbVisibleSmallScreen ? false : isLargeBreakpoint
  const isSidebarMenuEnabled = process.env.IS_SIDEBAR_MENU_ENABLED === 'enabled'
  const isBreadCrumbEnabled = process.env.IS_BREADCRUMB_ENABLED === 'enabled'

  // TODO: Should be removed as soon as the design system includes the container-width tokens
  const containerClass = `${currentBrand}Container`
  const isFavouritesContainerXtraLarge =
    process.env.FAVOURITES_CONTAINER_EXTRA_LARGE_WIDTH_ENABLED === 'enabled'
  const isFavouritesContainerFullWidth =
    process.env.FAVOURITES_CONTAINER_FULL_WIDTH_ENABLED === 'enabled'

  const getHeadingComponent = (headingStyle: string): React.ReactElement => {
    if (!shouldEnabledNS) {
      let headingComponent
      switch (headingStyle) {
        case 'xLarge':
          headingComponent = (
            <HeadingXLarge Tag="h1" center>
              {getLabelFromStorage('favorites.page.wishlist')}
            </HeadingXLarge>
          )
          break
        case 'large':
          headingComponent = (
            <HeadingLarge Tag="h1" center>
              {getLabelFromStorage('favorites.page.wishlist')}
            </HeadingLarge>
          )
          break
        case 'medium':
          headingComponent = (
            <HeadingMedium Tag="h1" center>
              {getLabelFromStorage('favorites.page.wishlist')}
            </HeadingMedium>
          )
          break
        case 'small':
          headingComponent = (
            <HeadingSmall Tag="h1" center>
              {getLabelFromStorage('favorites.page.wishlist')}
            </HeadingSmall>
          )
          break
        case 'xSmall':
          headingComponent = (
            <HeadingXSmall Tag="h1" center>
              {getLabelFromStorage('favorites.page.wishlist')}
            </HeadingXSmall>
          )
          break
        default:
          headingComponent = (
            <HeadingLarge Tag="h1" center>
              {getLabelFromStorage('favorites.page.wishlist')}
            </HeadingLarge>
          )
      }
      return headingComponent
    }
    return isLargeBreakpoint ? (
      <HeadingLargeNS Tag="h1" className={styles.NSheadingTitle}>
        {getLabelFromStorage('favorites.page.wishlist')}
      </HeadingLargeNS>
    ) : (
      <HeadingXLargeNS Tag="h1" className={styles.NSheadingTitle}>
        {getLabelFromStorage('favorites.page.wishlist')}
      </HeadingXLargeNS>
    )
  }
  const getMemoizedHeadingComponent = React.useMemo(() => getHeadingComponent(HeadingTextSize), [])

  // react-remove-scrollbar package is adding margin-right:0!important in the body, which is
  // shifting the page content to the right on opening of size picker. To mitigate we have to
  // add bellow code only specific to otherStories brand.
  useEffect(() => {
    if (currentBrand === BRAND_OTHER_STORIES) {
      document.body.style.setProperty('margin-right', 'auto', 'important')
      document.body.style.setProperty('padding', '0', '')
    }
  }, [])

  return (
    <>
      {shouldEnabledNS ? (
        <ContainerNS hasFullWidth Tag="main">
          <header className={styles.NSheading}>{getMemoizedHeadingComponent}</header>
          <FavouritesError />
          {isStyleboardsEnabled ? <Stylehub /> : <FavouritesItemsList shouldSendTrackingOnLoad />}
        </ContainerNS>
      ) : (
        <Container
          Tag="main"
          large
          id="main-content"
          style={{ marginTop: `${getFixedHeaderHeight()}px` }}
          className={cn(styles.container, styles[containerClass])}
          xlarge={isFavouritesContainerXtraLarge}
          fullWidth={isFavouritesContainerFullWidth}
        >
          {isBreadCrumbEnabled && !isNorthstarBrand && (
            <MemoizedBreadcrumbs
              links={breadcrumbsLinks}
              shouldHideBreadcrumbs={shouldHideBreadcrumbs}
              className={cn(
                isBreadCrumbFullWidth && styles.breadCrumbFullWidth,
                styles[`${currentBrand}BreadCrumb`],
              )}
            />
          )}
          <header
            className={cn(
              styles.heading,
              styles[`${currentBrand}Heading`],
              isSidebarMenuEnabled && styles.favouriteHeadingWithSidebarMenu,
              (isStyleboardsEnabled || isMyClosetEnabled) && styles.extraBottomMargin,
            )}
          >
            {getMemoizedHeadingComponent}
          </header>
          <FavouritesError />

          {isStyleboardsEnabled || isMyClosetEnabled ? (
            <Stylehub />
          ) : (
            <FavouritesItemsList shouldSendTrackingOnLoad />
          )}
        </Container>
      )}
      {isBreadCrumbEnabled && isNorthstarBrand && (
        <MemoizedBreadcrumbs
          links={breadcrumbsLinks}
          shouldHideBreadcrumbs={shouldHideBreadcrumbs}
          shouldUseBackground
          className={cn(
            isBreadCrumbFullWidth && styles.breadCrumbFullWidth,
            isBreadCrumbFullWidth && styles[`${currentBrand}BreadCrumb`],
          )}
        />
      )}
    </>
  )
}

export default Overview
